import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { BarChart3, TrendingUp, Award, Calendar } from 'lucide-react';

const ProgressPage = () => {
  const [progress, setProgress] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      setError(null);
      // TODO: Replace with actual API call
      // const res = await parentAPI.getChildrenProgress();
      // setProgress(res.data.data || []);
      
      // Placeholder data for now
      setProgress([]);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchProgress();
  }, []);

  const progressMetrics = [
    { id: 'overall', name: 'Overall Progress', value: '85%', trend: '+5%', icon: TrendingUp },
    { id: 'attendance', name: 'Attendance', value: '92%', trend: '+2%', icon: Calendar },
    { id: 'grades', name: 'Average Grade', value: 'B+', trend: '+0.3', icon: Award },
    { id: 'assignments', name: 'Assignments', value: '78%', trend: '+8%', icon: BarChart3 }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchProgress}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Academic Progress</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            This Month
          </Button>
          <Button variant="outline" size="sm">
            Export Report
          </Button>
        </div>
      </div>

      {/* Progress Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {progressMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <Card key={metric.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                    <p className="text-sm text-green-600">{metric.trend} from last month</p>
                  </div>
                  <Icon className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subject-wise Progress */}
        <Card>
          <CardHeader>
            <CardTitle>Subject-wise Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {(!progress || progress.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No progress data available</p>
                <p className="text-sm">Progress will appear once your child starts attending classes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {progress.map((subject) => (
                  <div key={subject.id} className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{subject.name}</h4>
                      <p className="text-sm text-gray-600">{subject.grade}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{subject.percentage}%</p>
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${subject.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Achievements */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Achievements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No recent achievements</p>
              <p className="text-sm">Achievements will appear here when earned</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProgressPage;
